import { useState, useEffect, useRef } from "react"
import styled from "styled-components"
import { Filter } from 'react-feather'
import Button from "components/_UI/Button"
import Fieldset from "components/_UI/Fieldset"
import Text from "components/_UI/Text"
import Calendar from 'react-calendar'
import colors from "theme/colors"
import breakpoints from "theme/breakpoints"
import Select from 'react-select';
import date from "helpers/date"
import promotionalCodesApi from "api/requests/promotionalCodes"
import { useEvent } from "context/events/Event"
import { useMemo } from 'react';
import { Column } from "styled/alignment/Column"
import Icon from "components/_UI/Icon"
import ticketsApi from "api/requests/tickets"
import debounce from 'helpers/debounce';

export default function ParticipantsFilters({
    onApplyFilter
}) {
    const { event } = useEvent();

    const [filters, setFilters] = useState({
        search: '',
        type: '',
        status: '',
        dateRange: [],
        coupons: [],
        tickets: new Set(),
    });

    const [showFilters, setShowFilters] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [couponsLoading, setCouponsLoading] = useState(false);
    const [couponOptions, setCouponOptions] = useState([]);
    const [itemsLoading, setItemsLoading] = useState(false);
    const [itemOptions, setItemOptions] = useState([]);

    const arrayToCommaString = (value) =>
        (Array.isArray(value) && value.length > 0) || (value instanceof Set && value.size > 0)
            ? [...value].join(', ')
            : null;

    const safeDate = (date) =>
        date instanceof Date && !isNaN(date) ? date.toISOString().split('T')[0] : null;

    const removeFalsyValues = (obj) => {
        return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0) && !(v instanceof Set && v.size === 0)));
    };

    const selectedValues = useMemo(() => {
        const values = {
            type: filters?.type || '',
            used: filters?.status || '',
            coupons: arrayToCommaString(filters?.coupons),
            tickets: arrayToCommaString(filters?.tickets),
            start_date: safeDate(filters?.dateRange?.[0]),
            end_date: safeDate(filters?.dateRange?.[1]),
            term: filters?.search?.trim() || null,
        };

        return removeFalsyValues(values);
    }, [filters]);

    const popoverRef = useRef(null)

    const productTypes = [
        {
            name: 'Ingresso',
            label: 'Ingresso',
            value: 'ticket'
        },
        {
            name: 'Produto',
            label: 'Produto',
            value: 'product'
        },
        {
            name: 'Todos',
            label: 'Todos',
            value: ''
        }
    ]

    const productStatus = [
        {
            name: 'Não Utilizado',
            label: 'Não utilizado',
            value: '0'
        },
        {
            name: 'Utilizado',
            label: 'Utilizado',
            value: '1'
        },
        {
            name: 'Todos',
            label: 'Todos',
            value: ''
        }
    ]

    function clearFilters() {
        setFilters({
            search: '',
            type: '',
            status: '',
            coupons: [],
            tickets: new Set(),
            dateRange: [],
        })
    }

    const hasActiveFilters = filters.search.length > 0
        || filters?.type?.length
        || filters?.status?.length > 0
        || filters.dateRange.length > 0
        || filters.coupons.size > 0
        || filters.tickets.size > 0

    const activeFiltersCount = [
        filters.search.length ? 1 : 0,
        filters.type.length ? 1 : 0,
        filters.status.length ? 1 : 0,
        filters.dateRange.length ? 1 : 0,
        filters.coupons.size ? 1 : 0,
        filters.tickets.size ? 1 : 0,
    ].reduce((a, b) => a + b, 0)

    const handleTermChange = debounce((searchTerm) => {
        setFilters(prev => ({
            ...prev,
            search: searchTerm
        }));
    }, 500)

    async function getCoupons() {
        setCouponsLoading(true);

        try {
            const { data } = await promotionalCodesApi.getByEvent(event?.slug);

            const formattedCoupons = data.map(coupon => ({
                value: coupon.code,
                label: coupon.code
            }));

            setCouponOptions(formattedCoupons);
        } catch (error) {
            console.error("Erro ao buscar cupons:", error);
        } finally {
            setCouponsLoading(false);
        }
    }

    async function getItems(type) {
        try {
            setItemsLoading(true);
            const { data } = await ticketsApi.getAllItems(event?.slug, {
                type,
                deleted: 'include'
            });

            const formattedItems = data.map(item => ({
                value: item.id,
                label: item.name
            }));

            setItemOptions(formattedItems);
        } catch (error) {
            console.error("Erro ao buscar itens:", error);
        } finally {
            setItemsLoading(false);
        }
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
        };

        if (showCalendar) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showCalendar]);

    useEffect(() => {
        onApplyFilter(selectedValues);
    }, [selectedValues]);

    useEffect(() => {
        if (filters.type && filters.type !== '') {
            getItems(filters.type);
        }
    }, [filters.type]);

    useEffect(() => {
        getCoupons();
    }, []);

    return (
        <Container>
            <FiltersContainer>
                <FiltersRow>
                    <SearchContainer>
                        <Fieldset
                            placeholder="Buscar por nome, e-mail ou código..."
                            layout='squired'
                            style={{ paddingRight: "2rem" }}
                            marginBottom='6px'
                            onChangeValue={(e) => handleTermChange(e)}
                        />
                    </SearchContainer>

                    <FilterButton>
                        <Button
                            variant='featured'
                            category='squired'
                            style={{ position: 'relative' }}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            {
                                <>
                                    <Filter color={colors.neutralLight} size={16} />
                                    <Text
                                        color={colors.neutralLight}
                                        paddingBottom={0}
                                        marginLeft='8px'
                                    >
                                        Filtros
                                    </Text>
                                    <Badge>{activeFiltersCount}</Badge>
                                </>
                            }
                        </Button>
                    </FilterButton>

                    <ClearFiltersButton>
                        <Button
                            variant={hasActiveFilters ? 'light' : 'disabled'}
                            category='squired'
                            onClick={clearFilters}
                        >
                            <Column width='18px' height='18px'>
                                <Icon name="filter-off"  color={colors.night} />
                            </Column>
                            <Text paddingBottom={0} marginLeft={'8px'}>Limpar filtros</Text>
                        </Button>
                    </ClearFiltersButton>
                </FiltersRow>

                {showFilters && (
                    <ExpandedFilters>
                        <FilterGroup>
                            <Text paddingBottom={'0'}>Tipo</Text>
                            <Select
                                placeholder='Selecione'
                                options={productTypes}
                                noOptionsMessage={() => 'Nenhuma opção disponível'}
                                value={productTypes.find(opt => opt.value === filters.type)}
                                onChange={(selected) => {
                                    setFilters(prev => ({ ...prev, type: selected ? selected.value : '' }))
                                }}
                            />
                        </FilterGroup>

                        {filters.type && filters.type !== '' && (
                            <FilterGroup>
                                <Text paddingBottom={'0'}>
                                    {filters.type === 'ticket' ? 'Selecionar ingresso' : 'Selecionar produto'}
                                </Text>
                                <Select
                                    isMulti
                                    placeholder={itemsLoading ? 'Carregando...' : 'Selecione'}
                                    options={itemOptions}
                                    noOptionsMessage={() => itemsLoading ? 'Carregando...' : `Nenhum ${filters.type === 'ticket' ? 'ingresso' : 'produto'} disponível`}
                                    value={[...filters.tickets].map(id => itemOptions.find(opt => opt.value === id)).filter(Boolean)}
                                    onChange={(selected) => {
                                        const selectedSet = new Set(selected.map(opt => opt.value));
                                        setFilters(prev => ({ ...prev, tickets: selectedSet }));
                                    }}
                                />
                            </FilterGroup>
                        )}

                        <FilterGroup>
                            <Text paddingBottom={'0'}>Status</Text>
                            <Select
                                placeholder='Selecione'
                                options={productStatus}
                                noOptionsMessage={() => 'Nenhuma opção disponível'}
                                value={productTypes.find(opt => opt.value === filters.status)}
                                onChange={(selected) => {
                                    setFilters(prev => ({ ...prev, status: selected ? selected.value : '' }))
                                }}
                            />
                        </FilterGroup>
                        <FilterGroup>
                            <Text paddingBottom={0}>Data</Text>
                            <CalendarButton onClick={() => setShowCalendar(!showCalendar)}>
                                <div
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                >
                                    <Select
                                        isMulti
                                        placeholder={
                                            Array.isArray(filters.dateRange) && filters.dateRange.length === 2
                                                ? `${date.format(filters.dateRange[0])} até ${date.format(filters.dateRange[1])}`
                                                : 'Selecione o intervalo de datas'}
                                        options={[]}
                                        isSearchable={false}
                                        menuIsOpen={false}
                                    />
                                </div>

                                {showCalendar && (
                                    <CalendarPopover
                                        ref={popoverRef}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Calendar
                                            selectRange={true}
                                            value={filters.dateRange}
                                            onChange={(d) => setFilters(prev => ({ ...prev, dateRange: d }))}
                                        />
                                        <CalendarFooter>
                                            <Button
                                                marginBottom="0"
                                                variant={filters.dateRange.length === 0 ? 'disabled' : 'featured'}
                                                onClick={() => setFilters(prev => ({ ...prev, dateRange: [] }))}
                                            >
                                                Limpar
                                            </Button>
                                        </CalendarFooter>
                                    </CalendarPopover>
                                )}
                            </CalendarButton>
                        </FilterGroup>

                        <FilterGroup>
                            <Text paddingBottom={'0'}>Cupons</Text>
                            <Select
                                isMulti
                                placeholder={couponsLoading ? 'Carregando...' : 'Selecione'}
                                options={couponOptions}
                                noOptionsMessage={() => couponsLoading ? 'Carregando...' : 'Nenhum cupom disponível'}
                                value={[...filters.coupons].map(value => ({ value, label: value }))}
                                onChange={(selected) => {
                                    const selectedSet = new Set(selected.map(opt => opt.value));
                                    setFilters(prev => ({ ...prev, coupons: selectedSet }));
                                }}
                            />

                        </FilterGroup>
                    </ExpandedFilters>
                )}
            </FiltersContainer>
        </Container>
    )
}

const Container = styled.div`
  width: 100%;
  margin-top: 20px;
`

const FiltersContainer = styled.div`
  margin-bottom: 1.5rem;
`

const FiltersRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
  
  @media screen and (min-width: ${breakpoints.md}) {
    flex-direction: row;
  }
`

const Badge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: ${colors.night};
  color: white;
  border-radius: 50%;
  padding: 5px 8px;
  font-size: 12px;
  min-width: 20px;
  text-align: center;
`;

const SearchContainer = styled.div`
  flex: 1;
  width: 100%;
`

const FilterButton = styled.div`
  width: 100%;
  
  @media screen and (min-width: ${breakpoints.lg}) {
    max-width: 150px;
    width: 25%;
  }
`

const CalendarButton = styled.div`
    position: relative;
    width: 100%;
`

const CalendarPopover = styled.div`
    position: absolute;
    top: 100%;
    left: 50%;
    z-index: 99;
    transform: translateX(-50%);
    padding: 1rem;
    border-radius: 6px;
    background-color: white;
    box-shadow:  0 0 #0000, 0 0 #0000, 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        right: 0;
        left: auto;
        transform: none;
    }

    .react-calendar {
        border: none;
    }
`

const CalendarFooter = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: space-between;
`

const ClearFiltersButton = styled.div`
    width: 100%;
    @media screen and (min-width: ${breakpoints.lg}) { 
        width: 176px;
    }

`

const ExpandedFilters = styled.div`
  margin-top: 0.75rem;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  
  @media screen and (min-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media screen and (min-width: ${breakpoints.md}) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`